import React from "react";
import { motion } from "framer-motion";
import { Phone, Mail, Instagram, Facebook, MapPin } from "lucide-react";

const contacts = [
  {
    label: "Telefon",
    value: "0738 711 661",
    href: "tel:0738711661",
    icon: Phone,
    testid: "contact-phone",
  },
  {
    label: "Email",
    value: "elitecenterhunedoara@yahoo.com",
    href: "mailto:elitecenterhunedoara@yahoo.com",
    icon: Mail,
    testid: "contact-email",
  },
  {
    label: "Instagram",
    value: "Elite Center Hunedoara",
    href: "https://instagram.com/elitecenterhunedoara",
    icon: Instagram,
    testid: "contact-instagram",
  },
  {
    label: "Facebook",
    value: "Elite Center Hunedoara",
    href: "https://facebook.com/elitecenterhunedoara",
    icon: Facebook,
    testid: "contact-facebook",
  },
];

const FooterContact = () => {
  return (
    <footer
      id="contact"
      className="border-t border-[rgba(242,235,229,0.1)] bg-[#050505] relative overflow-hidden"
      data-testid="footer-section"
    >
      {/* Massive brutalist heading strip */}
      <div className="section !pb-12">
        <div className="container-brutal grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-3">
            <span className="eyebrow">§ 07 / Contact</span>
          </div>
          <div className="col-span-12 md:col-span-9">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="section-title"
            >
              Vino în<br />
              <span className="text-[#D91604]">sală.</span>
            </motion.h2>
            <p className="editorial-lead mt-8 max-w-xl text-[#8a8a8a]">
              Prima ședință este gratuită. Adu-ți doar dorința de a învăța și un
              echipament lejer — restul îl învățăm împreună, pe tatami.
            </p>
          </div>
        </div>
      </div>

      {/* Contact rows */}
      <div className="container-brutal border-t border-[rgba(242,235,229,0.1)]" data-testid="contact-list">
        {contacts.map((c) => {
          const Icon = c.icon;
          return (
            <a
              key={c.label}
              href={c.href}
              target={c.href.startsWith("http") ? "_blank" : undefined}
              rel="noreferrer"
              className="group grid grid-cols-12 items-center gap-4 px-6 md:px-12 py-6 md:py-8 border-b border-[rgba(242,235,229,0.1)] transition-colors hover:bg-[#D91604] hover:text-[#050505]"
              data-testid={c.testid}
            >
              <div className="col-span-3 md:col-span-2 flex items-center gap-3">
                <Icon className="w-4 h-4 md:w-5 md:h-5" strokeWidth={1.5} />
                <span className="eyebrow group-hover:text-[#050505]/70">{c.label}</span>
              </div>
              <div className="col-span-8 md:col-span-9 font-anton text-2xl md:text-4xl uppercase tracking-tight truncate">
                {c.value}
              </div>
              <div className="col-span-1 text-right font-anton text-xl md:text-2xl opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-transform">
                →
              </div>
            </a>
          );
        })}
      </div>

      {/* Address + Map */}
      <div className="container-brutal grid grid-cols-12 gap-0 border-b border-[rgba(242,235,229,0.1)]">
        <div className="col-span-12 md:col-span-5 p-8 md:p-12 border-r border-[rgba(242,235,229,0.1)]">
          <div className="flex items-center gap-2 mb-6">
            <MapPin className="w-4 h-4 text-[#D91604]" strokeWidth={2} />
            <span className="eyebrow">Dojo · Localizare</span>
          </div>
          <h3 className="font-anton text-3xl md:text-5xl leading-none uppercase" data-testid="footer-address">
            Blocul 5,<br />
            Str. Victoriei 17,<br />
            <span className="text-[#D91604]">Hunedoara</span> · 331071
          </h3>
          <p className="text-sm tracking-widest uppercase text-[#8a8a8a] mt-4">
            (Colegiul Tehnic Matei Corvin)
          </p>
          <p className="font-editorial italic text-lg text-[#8a8a8a] mt-8 max-w-sm">
            Parcare gratuită la stradă. Intrarea din spatele blocului, urmează indicatoarele roșii.
          </p>
          <a
            href="https://www.google.com/maps/search/?api=1&query=Elite+Center+Hunedoara"
            target="_blank"
            rel="noreferrer"
            data-testid="footer-directions"
            className="inline-flex mt-8 items-center gap-3 font-anton text-sm tracking-widest uppercase px-5 py-3 border border-[rgba(242,235,229,0.22)] hover:bg-[#f2ebe5] hover:text-[#050505] transition-colors"
          >
            Deschide harta →
          </a>
        </div>
        <div className="col-span-12 md:col-span-7 min-h-[380px] md:min-h-0 relative">
          <iframe
            title="Elite Center Hunedoara — Harta"
            src="https://www.google.com/maps?q=Elite+Center+Hunedoara&z=16&output=embed"
            width="100%"
            height="100%"
            className="w-full h-full min-h-[380px] grayscale contrast-125 opacity-90 hover:grayscale-0 hover:opacity-100 transition-all duration-700"
            style={{ border: 0, filter: "grayscale(1) contrast(1.2) invert(0.92) hue-rotate(180deg)" }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="footer-map"
          />
          <div className="absolute top-4 left-4 bg-[#050505]/85 backdrop-blur px-3 py-2 border border-[rgba(242,235,229,0.15)] pointer-events-none">
            <div className="eyebrow">Dojo</div>
            <div className="font-anton text-lg">45°45′N · 22°54′E</div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="container-brutal px-6 md:px-12 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full overflow-hidden bg-white ring-1 ring-[#D91604]/40 shrink-0">
            <img
              src="https://customer-assets.emergentagent.com/job_elite-karate-3d/artifacts/17qa70pq_sigla%20club%202015-1.webp"
              alt="Elite Center Hunedoara — siglă"
              className="w-full h-full object-cover scale-[1.06]"
              data-testid="footer-logo"
            />
          </div>
          <span className="font-anton text-sm tracking-widest">
            © {new Date().getFullYear()} ELITE CENTER HUNEDOARA<br className="md:hidden" />
            <span className="hidden md:inline"> · </span>TOATE DREPTURILE REZERVATE
          </span>
        </div>
        <div className="flex items-center gap-6 text-[11px] tracking-[0.28em] uppercase text-[#8a8a8a]">
          <span className="font-jp text-base text-[#f2ebe5]">押忍</span>
          <span>Karate Shotokan</span>
          <span className="hidden md:inline">·</span>
          <span>Mixed Martial Arts</span>
        </div>
      </div>
    </footer>
  );
};

export default FooterContact;
